import { FC, useEffect, useState } from 'react';

import { daysFromNow, parse, isValid, DATE_FORMAT } from '@eneco/common/date';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';
import { usePlaceholderContent } from '@sitecore/common';
import { NewDeliveryDateStepRendering } from '@sitecore/types/NewDeliveryDateStep';

import { FlowConsumerContextEnergy } from '../../../context';
import { DeliveryDateFormFields, DeliveryDateMovingNotification, DeliveryDatePicker } from '../../../shared/components';
import { StepShell } from '../../../shared/components/StepShell/StepShell';
import {
  getMinContractStartDateInDays,
  getMaxContractStartDateInDays,
  getMaxUrgentContractStartDateInDays,
  CONTRACT_CONSIDERATION_PERIOD_IN_DAYS,
  buildDeliveryDateFormSchema,
} from '../../../shared/helpers';
import { useFlowStep } from '../../../shared/hooks';

export const DeliveryDateStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const {
    context: { isSME, isMoving, consentConsiderationPeriod, deliveryDate },
  } = useFlowSelector(state => state);

  const { label } = useDC();
  const { format, date } = useFormatter();

  const minimumDays = getMinContractStartDateInDays(!!isMoving, !!isMoving, label);
  const maximumDays = getMaxContractStartDateInDays(label);
  const maximumUrgentDays = getMaxUrgentContractStartDateInDays(label);
  const minContractStartDateInDays = getMinContractStartDateInDays(!!isMoving, false, label);

  const minimumDate = daysFromNow(minimumDays);
  const maximumDate = daysFromNow(maximumDays);
  const defaultDate = date.short(daysFromNow(minContractStartDateInDays));

  const [shouldShowExtraField, setShouldShowExtraField] = useState(
    !!(consentConsiderationPeriod && isMoving && !isSME),
  );

  const { NewDeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    NewDeliveryDateStep: NewDeliveryDateStepRendering;
  }>();

  const isDateValid = (fallbackDate: string, selectedDate?: string): string => {
    if (!selectedDate) return fallbackDate;

    const [day, month, year] = selectedDate.split('-').map(Number);
    const targetDate = new Date(year, month - 1, day);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= minContractStartDateInDays ? selectedDate : fallbackDate;
  };

  const {
    form: {
      handleSubmit,
      setValue,
      register,
      watch,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<DeliveryDateFormFields>({
    buildSchema: () =>
      buildDeliveryDateFormSchema({
        isMoving,
        minimumDate,
        maximumDate,
        minimumDays,
        shouldShowExtraField,
        format,
        dateShort: date.short,
        textualData,
      }),
    defaultValues: () => ({
      deliveryDate: isDateValid(defaultDate, deliveryDate),
      consentConsiderationPeriod,
    }),
    onSubmitSuccess: formField => ({
      ...formField,
      isUrgent: formField.deliveryDate
        ? parse(formField.deliveryDate, DATE_FORMAT) <= daysFromNow(maximumUrgentDays)
        : false,
    }),
  });

  const mutatedDeliveryDate = watch('deliveryDate') || '';

  useEffect(() => {
    if (isMoving && !isSME) {
      if (mutatedDeliveryDate) {
        const currentDate = parse(mutatedDeliveryDate, DATE_FORMAT);
        setShouldShowExtraField(
          isValid(currentDate) && currentDate <= daysFromNow(CONTRACT_CONSIDERATION_PERIOD_IN_DAYS),
        );
      } else {
        setShouldShowExtraField(false);
      }
    }
  }, [isMoving, isSME, mutatedDeliveryDate]);

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <DeliveryDatePicker
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        shouldShowExtraField={shouldShowExtraField}
        mutatedDeliveryDate={mutatedDeliveryDate}
        textualData={{
          label: textualData?.deliveryDate?.deliveryDateFormField?.value?.label ?? '',
          placeholder: textualData?.deliveryDate?.deliveryDateFormField?.value?.placeholder,
          hint: textualData?.deliveryDate?.deliveryDateFormField?.value?.hint,
          extraFieldLabel: textualData?.deliveryDate?.isUrgentFormField?.value?.label ?? '',
        }}
      />

      <DeliveryDateMovingNotification isMoving={isMoving} textualData={textualData?.notifications} />
    </StepShell>
  );
};
